import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { SuccessDialog } from "./SuccessDialog";
import { ArrowLeft, BadgeCheck, Briefcase, Download, FileWarning, Landmark, Loader2, Mail, Pencil, Save, ShieldCheck, User } from "lucide-react";
import { API, authHdr } from "./AssociateFields";
import { SELF_PROF, fieldsFor, validateStep } from "./associateSpec";
import { TabSections } from "./TabSections";
import { RightsTab } from "./RightsTab";
import { DownloadsTab } from "./DownloadsTab";
import { LegacyDocs, legacyLeft } from "./LegacyDocs";
import { ChangesPanel } from "./ChangesPanel";

const EDITABLE = ["Associate Creation Pending", "Pending for Superuser Verification", "Rejected By Superuser", "Rejected By Associate"];
const TABS = [
  { key: "profile", label: "Profile", icon: User, steps: ["general", "entity", "photo"] },
  { key: "comm", label: "Communication", icon: Mail, steps: ["comm", "nominee"] },
  { key: "bank", label: "Bank Details", icon: Landmark, steps: ["bank"] },
  { key: "license", label: "Licenses & Certificates", icon: BadgeCheck, steps: ["license", "cert"] },
  { key: "other", label: "Other & Commercials", icon: Briefcase, steps: ["other", "commercials"] },
  { key: "rights", label: "Rights", icon: ShieldCheck, steps: [], custom: true },
  { key: "downloads", label: "Downloads", icon: Download, steps: [], custom: true },
  { key: "expired", label: "Expired & Outdated", icon: FileWarning, steps: [], custom: true, danger: true },
];

const prefill = (profile, intros) => {
  const d = {};
  Object.entries(profile).forEach(([k, v]) => { if (v && typeof v === "object" && !Array.isArray(v)) d[k] = { ...v }; });
  const g = d.general || {};
  if (!profile.other) d.other = { primary_color: "#1A3F3A", secondary_color: "#C8A96B" }; // Kinntegra defaults for a new associate
  if (g.introduced_by === "self" || (d.general && !g.introduced_by)) {
    const kw = intros.find((i) => i.code === "1994101");
    d.general = { ...g, introduced_by: kw ? kw.id : "self" }; // 1994101 itself (not in its own list) stays "Self"
  }
  const intro = intros.find((i) => i.id === (d.general || {}).introduced_by);
  if (["CA", "CS", "OTHER"].includes(g.profession) && intro) {
    const a = intro.arn || {};
    const emp = (intro.sales_employees || []).find((e) => e.id === (d.general || {}).introducer_employee);
    const eu = emp ? emp.euin || {} : { euin_name: a.euin_name || intro.name, euin_number: a.euin_number || intro.euin || "",
      euin_validity: a.euin_validity || "", euin_proof: a.euin_proof };
    d.license = { ...(d.license || {}), arn_name: a.arn_name || intro.name, arn_number: a.arn_number || "", arn_validity: a.arn_validity || "",
      arn_proof: a.arn_proof, euin_name: eu.euin_name || "", euin_number: String(eu.euin_number || "").replace(/^E/i, ""),
      euin_validity: eu.euin_validity || "", euin_proof: eu.euin_proof };
  }
  return d;
};

export default function AssociateWizard({ selfVerify = false }) {
  const { id: routeId, token } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [intros, setIntros] = useState([]);
  const [idx, setIdx] = useState(0);
  const [reached, setReached] = useState(0);
  const [draft, setDraft] = useState({});
  const [errs, setErrs] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState({ open: false, reason: "" });
  const [done, setDone] = useState(null);
  const [finished, setFinished] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const status = doc?.associate_status;
  const first = status === "Associate Creation Pending";
  const mode = selfVerify ? "self" : sp.get("mode") || (first ? "edit" : "view");
  const viewOnly = !selfVerify && mode === "view";
  const canEdit = !selfVerify && !viewOnly && mode !== "verify" && status !== "Pending for Associate Verification";
  const inFlow = EDITABLE.includes(status);
  const ro = !(canEdit && (first || editing));
  const id = doc?.id || routeId;
  useEffect(() => { if (mode === "edit" && canEdit) setEditing(true); }, [mode, canEdit]);

  useEffect(() => {
    (async () => {
      try {
        if (selfVerify) {
          const r = await axios.get(`${API}/associate-verify/${token}`);
          setIntros(r.data.introducers || []);
          setDoc({ ...r.data, id: r.data.id });
        } else {
          const [r, i] = await Promise.all([
            axios.get(`${API}/associates/${routeId}`, { headers: authHdr() }),
            axios.get(`${API}/associates/introducers`, { headers: authHdr() }),
          ]);
          setIntros(i.data.introducers.filter((x) => x.id !== routeId));
          setDoc(r.data);
        }
      } catch (e) { const m = e.response?.data?.detail || "Could not load associate"; toast.error(m); setLoadErr(m); }
    })();
  }, [routeId, token]); // eslint-disable-line

  const profile = useMemo(() => ({ ...(doc?.profile || {}) }), [doc]);
  useEffect(() => {
    if (!doc) return;
    setDraft(prefill(profile, intros));
    const done = TABS.findIndex((t) => !t.steps.some((s) => s in profile));
    setReached((r) => Math.max(r, done === -1 ? TABS.length : Math.max(done - 1, 0)));
  }, [doc, intros]); // eslint-disable-line

  const indiv = (draft.entity || profile.entity || {}).entity_type === "Individual";
  const ctx = { profile: { ...profile, ...draft }, introducers: intros, isFirst: intros.length === 0, isRoot: doc?.sub_broker_code === "1994101", id, token };
  const tabs = TABS.map((t) => ({ ...t, steps: t.steps.filter((s) => (indiv || !["cert", "nominee"].includes(s)) && fieldsFor(s, ctx).length) }))
    .filter((t) => (t.key === "expired" ? !first && !selfVerify && legacyLeft(profile).length > 0
      : t.custom ? !first && !selfVerify && !!doc?.sub_broker_code : t.steps.length)); // Rights/Downloads need a generated code
  const tab = tabs[Math.min(idx, tabs.length - 1)];

  ctx.onIfsc = async (key, val) => {
    if (!val || val.length !== 11) return;
    const pre = key.startsWith("ria_") ? "ria_" : "";
    try {
      const r = await axios.get(`${API}/associates/lookup/ifsc/${val}`, { headers: authHdr() });
      setStep("bank", { [`${pre}bank_name`]: r.data.bank_name, [`${pre}branch`]: r.data.branch, [`${pre}micr`]: r.data.micr });
    } catch (e) { toast.error(e.response?.data?.detail || "IFSC not found"); }
  };

  const setStep = (s, patch) => {
    setDraft((d) => {
      const next = { ...(d[s] || {}), ...patch };
      if (s === "general" && "introduced_by" in patch && next.profession && !(next.introduced_by && next.introduced_by !== "self") && !SELF_PROF.includes(next.profession)) next.profession = "";
      if (s === "general" && "introduced_by" in patch && patch.introduced_by !== (d.general || {}).introduced_by) next.introducer_employee = "";
      return { ...d, [s]: next };
    });
    setErrs((e) => { const n = { ...(e[s] || {}) }; Object.keys(patch).forEach((k) => delete n[k]); return { ...e, [s]: n }; });
  };

  const saveTab = async () => {
    if (ro || tab.custom) return true;
    const all = {};
    tab.steps.forEach((s) => { const e = validateStep(s, draft[s] || {}, ctx); if (Object.keys(e).length) all[s] = e; });
    setErrs(all);
    if (Object.keys(all).length) { toast.error("Please fix the highlighted fields"); return false; }
    setBusy(true);
    try {
      let rv = false;
      for (const s of tab.steps) {
        const payload = { ...(draft[s] || {}) };
        fieldsFor(s, ctx).forEach((fl) => { if (fl.compute) payload[fl.key] = fl.compute(); });
        const r = await axios.put(`${API}/associates/${id}/step/${s}`, payload, { headers: authHdr() });
        setDoc((dd) => ({ ...dd, profile: { ...dd.profile, [s]: payload }, ...(r.data.reverify ? { associate_status: r.data.associate_status, reverify: true } : {}) }));
        if (r.data.reverify) { rv = true; toast.warning("Re-verification started: login is blocked until the Superuser approves and the associate confirms the changes. Click Submit on the last tab.", { duration: 8000 }); }
      }
      if (rv) axios.get(`${API}/associates/${id}`, { headers: authHdr() }).then((r) => setDoc(r.data)).catch(() => {});
      return rv ? "reverify" : true;
    } catch (er) { toast.error(er.response?.data?.detail || "Save failed"); return false; } finally { setBusy(false); }
  };
  const goTab = async (i) => {
    if (i > idx && !(await saveTab())) return;
    setIdx(i); setReached((r) => Math.max(r, i)); setErrs({});
    window.scrollTo(0, 0);
  };
  const saveOnly = async () => { if (await saveTab()) toast.success("Saved"); };

  const act = async (url, body, after) => {
    if (!(await saveTab())) return;
    setBusy(true);
    try {
      const r = await axios.post(url, body || {}, { headers: selfVerify ? {} : authHdr() });
      setDone(after || r.data.message || "Done");
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };

  if (finished) return <div className="mx-auto mt-24 max-w-lg rounded-lg border p-8 text-center text-slate-700" data-testid="associate-self-finished">Thank you — your response has been recorded. You may close this page.</div>;
  if (!doc && loadErr) return <div className="mx-auto mt-24 max-w-lg rounded-lg border border-red-200 bg-red-50 p-8 text-center text-red-700" data-testid="associate-load-error">{loadErr}</div>;
  if (!doc || !tab) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const last = idx >= tabs.length - 1;
  const base = selfVerify ? `${API}/associate-verify/${token}` : `${API}/associates/${id}`;
  const verifyMode = mode === "verify" && status === "Pending for Superuser Verification";
  const selfMode = selfVerify && doc.can_act;
  const name = doc.name || doc.sub_broker_name;
  const teal = "bg-[#1a7f6f] hover:bg-[#166a5c]";

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="associate-wizard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!selfVerify && <button onClick={() => nav("/sub-brokers/list")} aria-label="Back" data-testid="associate-header-back-btn"
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 transition-colors hover:bg-gray-100"><ArrowLeft size={16} /></button>}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{selfVerify ? "Verify your details" : first ? "New Associate" : ro ? "View Associate" : "Edit Associate"}</h1>
            <p className="mt-0.5 text-xs text-gray-500">{tab.label}{!first && name ? ` — ${name}${doc.sub_broker_code ? ` (${doc.sub_broker_code})` : ""}` : ""}</p>
          </div>
        </div>
        <Badge variant="outline" className="border-teal-200 bg-teal-50 text-teal-800" data-testid="associate-status-badge">{status}</Badge>
      </div>
      {status === "Active" && editing && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="associate-reverify-notice">
          Saving changes to profile, communication, bank, nominee, licence or document details sends this associate for re-verification
          (Superuser, then the associate). Their login is blocked until they confirm. Colour and logo changes do not.
        </div>
      )}
      {doc.reverify && status !== "Active" && !selfVerify && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-900" data-testid="associate-reverify-banner">
          Re-verification in progress — login is blocked until the associate confirms the changes.
        </div>
      )}
      <ChangesPanel changes={doc.changes} ctx={ctx} />
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <Card className="min-w-0 flex-1 border-gray-200 shadow-sm">
          <CardContent className="p-4 md:p-6">
            <div className="mb-6 flex items-end justify-between gap-2 border-b">
              <div className="-mb-px flex gap-1 overflow-x-auto" role="tablist" data-testid="associate-tabs">
                {tabs.map((t, i) => {
                  const Icon = t.icon;
                  const allowed = !busy && (ro || !first || i <= reached + 1);
                  return (
                    <button key={t.key} type="button" role="tab" disabled={!allowed} onClick={() => goTab(i)} data-testid={`associate-tab-${t.key}`}
                      className={`flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm transition-colors ${t.danger ? (i === idx ? "border-rose-600 font-semibold text-rose-600" : "border-transparent font-medium text-rose-600 hover:text-rose-700")
                        : i === idx ? "border-teal-700 font-semibold text-teal-700" : "border-transparent text-gray-500 hover:text-gray-800 disabled:opacity-40"}`}>
                      <Icon className="h-3.5 w-3.5" />{t.label}
                    </button>);
                })}
              </div>
              {canEdit && !first && !editing && (
                <Button size="sm" variant="outline" className="mb-2 shrink-0" onClick={() => setEditing(true)} data-testid="associate-step-edit-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
              )}
            </div>
            {tab.key === "rights" && <RightsTab id={id} ro={ro} />}
            {tab.key === "downloads" && <DownloadsTab id={id} ro={ro && !(!selfVerify && !viewOnly && status === "Pending for Empanelment Form")} ctx={ctx} code={doc.sub_broker_code}
              status={status} onActivated={() => nav("/sub-brokers/list")} />}
            {tab.key === "expired" && <LegacyDocs ctx={ctx} profile={profile} />}
            {!tab.custom && <TabSections steps={tab.steps} draft={draft} setStep={setStep} ro={ro} errs={errs} ctx={ctx} profile={profile} />}
            <div className="mt-8 flex flex-wrap justify-between gap-2 border-t pt-4">
              <Button variant="outline" disabled={idx === 0 || busy} onClick={() => goTab(idx - 1)} data-testid="associate-back-btn">Back</Button>
              <div className="flex flex-wrap gap-2">
                {!ro && !tab.custom && <Button variant="outline" disabled={busy} onClick={saveOnly} data-testid="associate-save-btn"><Save className="mr-1.5 h-4 w-4" />Save</Button>}
                {!last && <Button className={`${teal} px-8`} onClick={() => goTab(idx + 1)} disabled={busy} data-testid="associate-proceed-btn">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{ro ? "Next" : "Save & Continue"}</Button>}
                {last && (verifyMode || selfMode) && (<>
                  <Button variant="destructive" disabled={busy} onClick={() => setReject({ open: true, reason: "" })} data-testid="associate-reject-btn">REJECT</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid="associate-accept-btn"
                    onClick={() => act(selfVerify ? `${base}/accept` : `${base}/su-accept`)}>ACCEPT</Button>
                </>)}
                {last && !verifyMode && !selfMode && canEdit && inFlow && (
                  <Button className={`${teal} px-8`} disabled={busy} onClick={() => act(`${base}/submit`)} data-testid="associate-submit-btn">Submit</Button>
                )}
                {last && !verifyMode && !selfMode && canEdit && !inFlow && !ro && (
                  <Button className={`${teal} px-8`} disabled={busy} data-testid="associate-save-finish-btn"
                    onClick={async () => { const ok = await saveTab(); if (ok === "reverify") act(`${base}/submit`); else if (ok) { toast.success("Changes saved"); setEditing(false); } }}>Save Changes</Button>
                )}
                {last && !verifyMode && !selfMode && (!canEdit || (!inFlow && ro)) && (
                  <Button className={`${teal} px-8`} onClick={() => (selfVerify ? setDone("Thank you.") : nav("/sub-brokers/list"))} data-testid="associate-finish-btn">Finish</Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={reject.open} onOpenChange={(o) => setReject((r) => ({ ...r, open: o }))}>
        <DialogContent data-testid="associate-reject-dialog">
          <DialogHeader><DialogTitle>Reason to Reject</DialogTitle></DialogHeader>
          <Textarea value={reject.reason} onChange={(e) => setReject((r) => ({ ...r, reason: e.target.value }))} placeholder="Rejection reason *" data-testid="associate-reject-reason" />
          <DialogFooter>
            <Button disabled={!reject.reason.trim() || busy} data-testid="associate-reject-submit"
              onClick={() => { setReject((r) => ({ ...r, open: false })); act(selfVerify ? `${base}/reject` : `${base}/su-reject`, { reason: reject.reason }, "Request rejected."); }}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SuccessDialog message={done} testid="associate-success-dialog"
        onClose={() => { setDone(null); if (!selfVerify) nav("/sub-brokers/list"); else setFinished(true); }} />
    </div>
  );
}

export const isSelfProfession = (p) => SELF_PROF.includes(p);
